import { isBefore } from 'date-fns';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTasks } from 'features/tasks';
import { useMemo } from 'react';
import {
  TreeNode,
  TreeNodeType,
} from 'shared/components/ui/collapsable-hierarchy-list';
import { useToday } from 'shared/contexts/today';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { Goal } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import {
  goalInboxID,
  lifeAreaOtherID,
  overdueSectionID,
  unlinkedPlannedTasksID,
} from 'shared/types/orderable-section';
import { GoalSortingMode, SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatOverdueId } from 'shared/utils/format-overdue-id';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getTaskDueDate } from 'shared/utils/get-task-due';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';
import { sortGoals } from 'shared/utils/sort-goals';
import { sortItems } from 'shared/utils/sort-items';

import { useExistingFilterTags } from './use-existing-filter-tags';

type CreateGoalTreeNodeOptions = {
  tasks: Task[];
  goals: Goal[];
  today: Timestamp;
  weekStartsOn: WeekDays;
  usedIds: Set<ID>;
};

const createGoalTreeNode = (
  goal: Goal,
  { goals, tasks, today, weekStartsOn, usedIds }: CreateGoalTreeNodeOptions,
): TreeNode => {
  const subGoals = goals.filter(({ id, parentIds }) => {
    if (parentIds?.[parentIds.length - 1] === goal.id && !usedIds.has(id)) {
      usedIds.add(id);
      return true;
    }
    return false;
  });

  const subGoalTreeNodes = subGoals.length
    ? sortGoals(subGoals, {
        allGoals: goals,
        sortMode: goal.goalSortingMode,
        customOrder: goal.goalSorting ?? [],
      }).map((subGoal) =>
        createGoalTreeNode(subGoal, {
          goals,
          tasks,
          today,
          weekStartsOn,
          usedIds,
        }),
      )
    : [];

  const sectionTreeNodes = goal.sections?.length
    ? goal.sections
        .filter(
          (section) =>
            section.tasks?.length &&
            section.tasks.some((id) => !usedIds.has(id)),
        )
        .map<TreeNode>((section) => ({
          label: section.name,
          type: TreeNodeType.Section,
          id: section.id,
          children: tasks
            .filter(({ id, goalId }) => {
              if (
                goalId === goal.id &&
                section.tasks?.includes(id) &&
                !usedIds.has(id)
              ) {
                usedIds.add(id);
                return true;
              }
              return false;
            })
            .map((task) => ({
              label: task.name,
              id: task.id,
              type: TreeNodeType.Task,
            })),
        }))
    : [];

  const ownTasks = getSortedTasks(
    tasks.filter(({ goalId, id }) => {
      if (
        goalId === goal.id &&
        !goal.sections?.some(({ tasks }) => tasks?.includes(id)) &&
        !usedIds.has(id)
      ) {
        usedIds.add(id);
        return true;
      }
      return false;
    }),
    {
      today,
      order: goal.taskSorting ?? [],
      mode: goal.taskSortingMode ? [goal.taskSortingMode] : [],
      weekStartsOn,
    },
  ).map((task) => ({ label: task.name, id: task.id, type: TreeNodeType.Task }));

  return {
    id: goal.id,
    type: TreeNodeType.Goal,
    label: goal.name,
    children: [...subGoalTreeNodes, ...sectionTreeNodes, ...ownTasks],
  };
};

export const useLifeAreaGroupingSections = () => {
  const today = useToday();
  const allLifeAreas = useLocalizedLifeAreas();
  const allGoals = useGoals();
  const allTasks = useTasks({ uncompletedOnly: true });
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  const existingFilteredTags = useExistingFilterTags();

  const tasks = useMemo(() => {
    const tasks = allTasks.filter(
      ({ action, type }) =>
        ![
          TaskActionType.GoalTimeslot,
          TaskActionType.LifeAreaTimeslot,
        ].includes(action?.type!) &&
        (!user?.settings?.allTasks?.hideHabits || type !== TaskType.Habit),
    );

    if (existingFilteredTags.length) {
      const tagTaskIds = existingFilteredTags
        .map((tagId) =>
          relationshipsMap[createPrefixId(tagId, IDPrefixes.Tag)]?.reduce<ID[]>(
            (acc, prefixedId) => {
              if (getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task) {
                acc.push(getPrefixedId(prefixedId));
              }

              return acc;
            },
            [],
          ),
        )
        .flat(1);

      return tasks.filter(
        ({ id, parentIds }) => !parentIds?.length && tagTaskIds.includes(id),
      );
    }

    return tasks.filter(
      ({ parentIds, archivedAt, completedAt }) =>
        !archivedAt && !completedAt && !parentIds?.length,
    );
  }, [
    allTasks,
    existingFilteredTags,
    relationshipsMap,
    user?.settings?.allTasks?.hideHabits,
  ]);

  const goals = useMemo(
    () =>
      allGoals.filter(
        ({ completedAt, archivedAt }) => !completedAt && !archivedAt,
      ),
    [allGoals],
  );

  const lifeAreas = useMemo(
    () => allLifeAreas.filter(({ archivedAt }) => !archivedAt),
    [allLifeAreas],
  );

  const items = useMemo(
    () => [...lifeAreas, ...goals, ...tasks],
    [goals, lifeAreas, tasks],
  );

  const treeNodes = useMemo(() => {
    const usedIds = new Set<ID>();

    const treeNodes = sortItems(
      lifeAreas,
      user?.settings?.lifeAreaSorting ?? [],
      'id',
    )
      .filter(({ id }) => {
        if (!usedIds.has(id)) {
          usedIds.add(id);
          return true;
        }
        return false;
      })
      .map<TreeNode>((lifeArea) => {
        const lifeAreaGoals = goals.filter(({ lifeAreaId, id, parentIds }) => {
          if (
            !parentIds?.length &&
            lifeAreaId === lifeArea.id &&
            !usedIds.has(id)
          ) {
            usedIds.add(id);
            return true;
          }
          return false;
        });

        const lifeAreaGoalTreeNodes = lifeAreaGoals.length
          ? sortGoals(lifeAreaGoals, {
              allGoals,
              sortMode: GoalSortingMode.Custom,
              customOrder: lifeArea.goalSorting,
            }).map((goal) =>
              createGoalTreeNode(goal, {
                goals,
                tasks,
                today,
                weekStartsOn,
                usedIds,
              }),
            )
          : [];

        const lifeAreaTasks = tasks.filter(({ id, lifeAreaId }) => {
          if (lifeAreaId === lifeArea.id && !usedIds.has(id)) {
            usedIds.add(id);
            return true;
          }
          return false;
        });

        const lifeAreaTaskTreeNodes = lifeAreaTasks.length
          ? getSortedTasks(lifeAreaTasks, {
              today,
              order: lifeArea.taskSorting ?? [],
              mode: [SortingMode.Custom],
              weekStartsOn,
            }).map<TreeNode>((task) => ({
              id: task.id,
              type: TreeNodeType.Task,
              label: task.name,
            }))
          : [];

        return {
          id: lifeArea.id,
          label: lifeArea.name,
          type: TreeNodeType.LifeArea,
          children: [...lifeAreaGoalTreeNodes, ...lifeAreaTaskTreeNodes],
        };
      }, []);

    const plannedUnlinkedTasks: ID[] = [];
    const inboxTasks: ID[] = [];
    const overdueTasks: ID[] = [];

    tasks.forEach((task) => {
      const dueDate = getTaskDueDate(task, { today, weekStartsOn });
      if (dueDate && isBefore(dueDate, today)) {
        overdueTasks.push(task.id);
      }

      if (usedIds.has(task.id) || task.goalId || task.lifeAreaId) {
        return;
      }

      if (
        task.schedules?.length ||
        task.endStrategy?.deadline ||
        task.deadlineDeadline
      ) {
        plannedUnlinkedTasks.push(task.id);
        usedIds.add(task.id);
        return;
      }

      if (!task.archivedAt && !task.completedAt && !task.parentIds?.length) {
        inboxTasks.push(task.id);
        usedIds.add(task.id);
      }
    });

    const lifeAreaLessGoals = goals.filter(({ id, lifeAreaId }) => {
      if (!lifeAreaId && !usedIds.has(id)) {
        usedIds.add(id);
        return true;
      }
      return false;
    });

    if (lifeAreaLessGoals.length || plannedUnlinkedTasks.length) {
      treeNodes.push({
        id: lifeAreaOtherID,
        label: lifeAreaOtherID,
        type: TreeNodeType.LifeArea,
        children: [
          ...(plannedUnlinkedTasks.length
            ? [
                {
                  id: unlinkedPlannedTasksID,
                  label: unlinkedPlannedTasksID,
                  type: TreeNodeType.LifeArea,
                  children: getSortedTasks(
                    tasks.filter(({ id }) => plannedUnlinkedTasks.includes(id)),
                    {
                      today,
                      order:
                        user?.settings?.allTasks?.unlinkedPlannedTasksSorting ??
                        [],
                      mode: [SortingMode.Custom],
                      weekStartsOn,
                    },
                  ).map<TreeNode>((task) => ({
                    id: task.id,
                    type: TreeNodeType.Task,
                    label: task.name,
                  })),
                },
              ]
            : []),
          ...lifeAreaLessGoals.map((goal) =>
            createGoalTreeNode(goal, {
              goals: allGoals,
              tasks,
              today,
              weekStartsOn,
              usedIds,
            }),
          ),
        ],
      });
    }

    return [
      ...(overdueTasks.length
        ? [
            {
              id: overdueSectionID,
              label: overdueSectionID,
              type: TreeNodeType.LifeArea,
              children: getSortedTasks(
                tasks.filter(({ id }) => overdueTasks.includes(id)),
                {
                  today,
                  order: user?.settings?.inboxSorting ?? [],
                  mode: [SortingMode.Custom],
                  weekStartsOn,
                },
              ).map<TreeNode>((task) => ({
                id: formatOverdueId(task.id),
                type: TreeNodeType.Task,
                label: task.name,
              })),
            },
          ]
        : []),
      {
        id: goalInboxID,
        label: goalInboxID,
        type: TreeNodeType.LifeArea,
        children: getSortedTasks(
          tasks.filter(({ id }) => inboxTasks.includes(id)),
          {
            today,
            order: user?.settings?.inboxSorting ?? [],
            mode: [SortingMode.Custom],
            weekStartsOn,
          },
        ).map<TreeNode>((task) => ({
          id: task.id,
          type: TreeNodeType.Task,
          label: task.name,
        })),
      },
      ...treeNodes,
    ];
  }, [
    allGoals,
    goals,
    lifeAreas,
    tasks,
    today,
    user?.settings?.allTasks?.unlinkedPlannedTasksSorting,
    user?.settings?.inboxSorting,
    user?.settings?.lifeAreaSorting,
    weekStartsOn,
  ]);

  return {
    treeNodes,
    items,
    tasks,
    goals,
    lifeAreas,
  };
};
